import { inject, Injectable, OnDestroy, PLATFORM_ID, signal, effect } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, Subject, catchError, of, switchMap, tap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { SignalRService } from '../signalr/signalr.service';
import { INotification, NotificationResponse } from '../../models/INotification';
import { AuthService } from '../../auth/auth.service';
import { SKIP_GLOBAL_LOADER } from '../../interceptors/loader.interceptor';
import { buildNotificationRoute } from '../../utils/notification-routing.helper';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private signalRService = inject(SignalRService);
  private authService = inject(AuthService);

  // Signals for reactive state
  notifications = signal<INotification[]>([]);
  unreadCount = signal<number>(0);
  isLoadingNotifications = signal<boolean>(false);
  isLoadingMore = signal<boolean>(false);
  isConnected = signal<boolean>(false);
  isConnecting = signal<boolean>(false);
  currentPage = signal<number>(1);
  hasMorePages = signal<boolean>(true);
  pageSize = signal<number>(50);

  // Subject for new notifications
  private newNotificationSubject = new Subject<INotification>();
  public newNotification$ = this.newNotificationSubject.asObservable();

  private baseUrl = environment.notificationUrl;
  private hubUrl = environment.notificationHub;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeSignalR();
    }
  }

  /**
   * Initialize SignalR connection when user is authenticated
   */
  private initializeSignalR(): void {
    let wasAuthenticated = false;

    // Monitor authentication state
    this.authService.authState$.subscribe((isAuthenticated) => {
      const isAuth = isAuthenticated ?? false;

      if (isAuth && !wasAuthenticated) {
        // Only connect when transitioning from not authenticated to authenticated
        this.connectSignalR();
      } else if (!isAuth && wasAuthenticated) {
        // Only disconnect when transitioning from authenticated to not authenticated
        this.disconnectSignalR();
        this.notifications.set([]);
        this.unreadCount.set(0);
      }

      wasAuthenticated = isAuth;
    });

    // Monitor SignalR connection state for unexpected disconnections
    effect(() => {
      const state = this.signalRService.connectionState();
      if (state === 'Disconnected' && this.isConnected()) {
        console.log('[NotificationService] SignalR connection lost unexpectedly');
        this.isConnected.set(false);

        // Attempt to reconnect if user is still authenticated
        if (wasAuthenticated) {
          console.log('[NotificationService] Attempting to reconnect...');
          setTimeout(() => this.connectSignalR(), 2000); // Wait 2 seconds before reconnecting
        }
      }
    });
  }

  /**
   * Register SignalR event handlers
   */
  private registerSignalRHandlers(): void {
    // Remove existing handler to prevent duplicates
    this.signalRService.off('ReceiveNotification');

    // Register handler for ReceiveNotification
    this.signalRService.on('ReceiveNotification', (notification: INotification) => {
      console.log('[NotificationService] New notification received:', notification);
      this.handleNewNotification(notification);
    });
  }

  /**
   * Connect to SignalR hub
   */
  private async connectSignalR(): Promise<void> {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting() || this.isConnected()) {
      return;
    }

    this.isConnecting.set(true);

    try {
      console.log('[NotificationService] Connecting to SignalR...');

      // Create connection
      const connection = this.signalRService.createConnection();

      // Register event handlers
      this.registerSignalRHandlers();

      // Start connection
      await this.signalRService.startConnection();
      this.isConnected.set(true);
      this.isConnecting.set(false);

      console.log('[NotificationService] SignalR connected successfully');

      // Load initial notifications
      this.loadNotifications().subscribe();
    } catch (error: any) {
      console.error('[NotificationService] Failed to connect to SignalR:', error);
      this.isConnected.set(false);
      this.isConnecting.set(false);

      // Check if it's an auth error
      if (error.statusCode === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        console.error('[NotificationService] Authentication failed - user may need to re-login');
      }

      // Fallback: still load notifications via API
      this.loadNotifications().subscribe();
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  private async disconnectSignalR(): Promise<void> {
    try {
      await this.signalRService.stopConnection();
      this.isConnected.set(false);
      this.isConnecting.set(false);
    } catch (error) {
      console.error('Error disconnecting SignalR:', error);
      this.isConnecting.set(false);
    }
  }

  /**
   * Handle new notification from SignalR
   */
  private handleNewNotification(notification: INotification): void {
    // Add to beginning of notifications list
    const currentNotifications = this.notifications();
    this.notifications.set([notification, ...currentNotifications]);

    // Update unread count
    if (!notification.isRead) {
      this.unreadCount.update(count => count + 1);
    }

    // Emit new notification event
    this.newNotificationSubject.next(notification);
  }

  /**
   * Load notifications from API
   */
  loadNotifications(pageIndex: number = 1, pageSize: number = 50, isLoadMore: boolean = false): Observable<NotificationResponse> {
    if (!isPlatformBrowser(this.platformId)) {
      return of({ value: { data: [], totalItems: 0, currentPage: 1, hasPreviousPage: false, hasNextPage: false, totalPages: 1, pageSize: 50 } });
    }

    // Set appropriate loading state
    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.isLoadingNotifications.set(true);
    }
    
    this.pageSize.set(pageSize);

    const endpoint = new URL('Notifications/my', this.baseUrl).toString();
    const params = new HttpParams()
      .set('page', pageIndex)
      .set('pageSize', pageSize);

    const context = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.get<NotificationResponse>(endpoint, { params, context }).pipe(
      switchMap((response: NotificationResponse) => {
        return of(response);
      }),
      tap((response) => {
        if (response && response.value && response.value.data) {
          // Update notifications
          if (pageIndex === 1) {
            this.notifications.set(response.value.data);
            this.currentPage.set(1);
          } else {
            // Append for pagination
            this.notifications.update(notifications => [...notifications, ...response.value.data]);
            // Update current page from response
            this.currentPage.set(response.value.currentPage);
          }

          // Update pagination state
          this.hasMorePages.set(response.value.hasNextPage);

          // Calculate unread count from all loaded notifications
          const allNotifications = this.notifications();
          const unread = allNotifications.filter(n => !n.isRead).length;
          this.unreadCount.set(unread);
        }
        
        // Clear appropriate loading state
        if (isLoadMore) {
          this.isLoadingMore.set(false);
        } else {
          this.isLoadingNotifications.set(false);
        }
      }),
      catchError((error) => {
        console.error('Error loading notifications:', error);
        this.hasMorePages.set(false);
        if (isLoadMore) {
          this.isLoadingMore.set(false);
        } else {
          this.isLoadingNotifications.set(false);
        }
        return of({ 
          value: { 
            data: [], 
            totalItems: 0, 
            currentPage: pageIndex, 
            hasPreviousPage: false, 
            hasNextPage: false, 
            totalPages: 0, 
            pageSize: pageSize 
          } 
        });
      })
    );
  }

  /**
   * Load more notifications (next page)
   */
  loadMoreNotifications(): Observable<NotificationResponse> {
    if (!this.hasMorePages() || this.isLoadingMore() || this.isLoadingNotifications()) {
      return of({ 
        value: { 
          data: [], 
          totalItems: 0, 
          currentPage: this.currentPage(), 
          hasPreviousPage: false, 
          hasNextPage: false, 
          totalPages: 0, 
          pageSize: this.pageSize() 
        } 
      });
    }

    // Calculate next page based on current page
    const nextPage = this.currentPage() + 1;
    
    // Call loadNotifications with isLoadMore flag
    return this.loadNotifications(nextPage, this.pageSize(), true);
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const url = `${this.baseUrl}Notifications/${notificationId}/mark-read`;

    const context = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.put(url, {}, { context }).pipe(
      tap(() => {
        // Update local state
        this.notifications.update(notifications =>
          notifications.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );

        // Update unread count
        this.unreadCount.update(count => Math.max(0, count - 1));
      }),
      catchError((error) => {
        console.error('Error marking notification as read:', error);
        return of(null);
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const url = `${this.baseUrl}Notifications/my/mark-all-read`;

    const context = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.put(url, {}, { context }).pipe(
      tap(() => {
        // Update local state
        this.notifications.update(notifications =>
          notifications.map(n => ({ ...n, isRead: true }))
        );

        // Reset unread count
        this.unreadCount.set(0);
      }),
      catchError((error) => {
        console.error('Error marking all notifications as read:', error);
        return of(null);
      })
    );
  }

  /**
   * Delete notification
   */
  deleteNotification(notificationId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const url = `${this.baseUrl}Notifications/${notificationId}`;

    const context = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    return this.http.delete(url, { context }).pipe(
      tap(() => {
        // Remove from local state
        const notification = this.notifications().find(n => n.id === notificationId);
        this.notifications.update(notifications =>
          notifications.filter(n => n.id !== notificationId)
        );

        // Update unread count if notification was unread
        if (notification && !notification.isRead) {
          this.unreadCount.update(count => Math.max(0, count - 1));
        }
      }),
      catchError((error) => {
        console.error('Error deleting notification:', error);
        return of(null);
      })
    );
  }

  /**
   * Refresh notifications
   */
  refreshNotifications(): void {
    this.currentPage.set(1);
    this.hasMorePages.set(true);
    this.loadNotifications().subscribe();
  }

  /**
   * Manually reconnect to SignalR hub
   * Useful when connection is lost and automatic reconnect failed
   */
  async reconnectSignalR(): Promise<void> {
    try {
      console.log('[NotificationService] Manual reconnect requested');
      await this.signalRService.reconnect();

      // Re-register handlers after reconnection
      this.registerSignalRHandlers();

      this.isConnected.set(true);
      console.log('[NotificationService] Manual reconnect successful');

      // Refresh notifications after reconnection
      this.loadNotifications().subscribe();
    } catch (error) {
      console.error('[NotificationService] Manual reconnect failed:', error);
      this.isConnected.set(false);
      throw error;
    }
  }

  /**
   * Build navigation URL from notification metadata
   * @param notification - Notification object
   * @returns Navigation URL string or null if cannot build route
   */
  getNotificationRoute(notification: INotification): string | null {
    return buildNotificationRoute(notification.metadata, notification.linkUrl);
  }

  ngOnDestroy(): void {
    this.disconnectSignalR();
    this.newNotificationSubject.complete();
  }
}

